import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "booking-datetime" }
const _hoisted_2 = { class: "row mb-3" }
const _hoisted_3 = { class: "col-6" }
const _hoisted_4 = ["name", "value", "disabled", "onInput", "onBlur"]
const _hoisted_5 = {
  key: 0,
  class: "invalid-feedback"
}
const _hoisted_6 = { class: "col-6" }
const _hoisted_7 = {
  key: 0,
  class: "invalid-feedback"
}
const _hoisted_8 = { class: "row mb-3" }
const _hoisted_9 = { class: "col-6" }
const _hoisted_10 = ["name", "value", "disabled", "onInput", "onBlur"]
const _hoisted_11 = {
  key: 0,
  class: "invalid-feedback"
}
const _hoisted_12 = { class: "col-6" }
const _hoisted_13 = {
  key: 0,
  class: "invalid-feedback"
}
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { class: "form-check" }
const _hoisted_16 = ["name", "checked", "disabled", "onChange", "onBlur"]
const _hoisted_17 = {
  key: 0,
  class: "mt-2"
}
const _hoisted_18 = {
  key: 0,
  class: "small"
}
const _hoisted_19 = { class: "small" }
const _hoisted_20 = {
  key: 0,
  class: "small"
}

import { computed, nextTick, ref, watch } from "vue";
import TimePickerPopover from "./TimePickerPopover.vue";
import { useMasterDataHelpers } from "../composables/useMasterDataHelpers";
import { useMyTransactionsQuery } from "../../api/queries/userQueries.js";


export default {
  __name: 'BookingDateTime',
  props: {
	form: {
		type: Object,
		required: true,
	},
	canIgnoreOpeningHours: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	show: {
		type: Boolean,
		default: false,
	},
},
  setup(__props) {

const props = __props;

// Extract the form instance for easier access in template
const { form } = props;

const masterData = useMasterDataHelpers();

const canCreateWithoutTransactionInRoom = (roomId) => {
	const room = masterData.getRoomById.value(roomId);
	return room?.permissions?.canCreateWithoutTransaction || false;
};

const isStudent = computed(() => masterData.userRole.value === "student");
const { data: budgetData } = useMyTransactionsQuery({ enabled: isStudent });

const availablePoints = computed(() => {
	const budget = budgetData.value;
	if (typeof budget?.totalPoints !== "number" || typeof budget?.usedPoints !== "number") {
		return null;
	}

	return Math.max(0, budget.totalPoints - budget.usedPoints);
});

const estimatedPoints = (values) => {
	if (!values.startDate || !values.startTime || !values.endDate || !values.endTime) {
		return 0;
	}
	const start = new Date(`${values.startDate}T${values.startTime}`);
	const end = new Date(`${values.endDate}T${values.endTime}`);
	const minutes = (end - start) / 60000;
	return minutes > 0 ? Math.ceil(minutes / 15) : 0;
};

// Duration preservation logic
const currentDuration = ref(null);
const endTimeUserModified = ref(false);

const roundToNearestQuarter = (timeString) => {
	if (!timeString) return timeString;
	
	const [hours, minutes] = timeString.split(":").map(Number);
	const roundedMinutes = Math.round(minutes / 15) * 15;
	
	if (roundedMinutes >= 60) {
		return `${(hours + 1).toString().padStart(2, "0")}:00`;
	}
	
	return `${hours.toString().padStart(2, "0")}:${roundedMinutes.toString().padStart(2, "0")}`;
};

const handleStartTimeChange = (field, value) => {
	// Round to nearest 15-minute interval
	const roundedValue = roundToNearestQuarter(value);
	
	// If currentDuration is null, calculate it BEFORE changing the field value
	if (currentDuration.value === null) {
		try {
			const startDate = form.getFieldValue("startDate");
			const currentStartTime = form.getFieldValue("startTime"); // Get CURRENT value before change
			const endDate = form.getFieldValue("endDate");
			const endTime = form.getFieldValue("endTime");

			if (startDate && currentStartTime && endDate && endTime) {
				const startDateTime = new Date(`${startDate}T${currentStartTime}`);
				const endDateTime = new Date(`${endDate}T${endTime}`);
				// Round duration to nearest 15-minute interval
				const duration = Math.round(
					(endDateTime - startDateTime) / (1000 * 60 * 15)
				) * 15;
				currentDuration.value = duration;
				endTimeUserModified.value = false;
			}
		} catch (error) {
			console.warn("Error calculating duration:", error);
		}
	}

	// Now change the field value with rounded time
	field.handleChange(roundedValue);

	// If we have a stored duration and end time hasn't been user-modified, adjust end time
	if (currentDuration.value && !endTimeUserModified.value) {
		nextTick(() => {
			try {
				const startDate = form.getFieldValue("startDate");
				const startTime = form.getFieldValue("startTime");

				if (startDate && startTime) {
					const startDateTime = new Date(`${startDate}T${startTime}`);
					const endDateTime = new Date(
						startDateTime.getTime() + currentDuration.value * 60 * 1000
					);

					const endDateStr = endDateTime.toISOString().split("T")[0];
					const endTimeStr = roundToNearestQuarter(endDateTime.toTimeString().slice(0, 5));

					form.setFieldValue("endDate", endDateStr);
					form.setFieldValue("endTime", endTimeStr);

					// Re-trigger validation to clear any errors
					form.validateField("endDate", "change");
					form.validateField("endTime", "change");
				}
			} catch (error) {
				console.warn("Error adjusting end time:", error);
			}
		});
	}
};

const handleStartDateChange = (field, value) => {
	field.handleChange(value);

	// If we have a stored duration and end time hasn't been user-modified, adjust end time
	if (currentDuration.value && !endTimeUserModified.value) {
		nextTick(() => {
			try {
				const startDate = form.getFieldValue("startDate");
				const startTime = form.getFieldValue("startTime");

				if (startDate && startTime) {
					const startDateTime = new Date(`${startDate}T${startTime}`);
					const endDateTime = new Date(
						startDateTime.getTime() + currentDuration.value * 60 * 1000
					);

					const endDateStr = endDateTime.toISOString().split("T")[0];
					const endTimeStr = roundToNearestQuarter(endDateTime.toTimeString().slice(0, 5));

					form.setFieldValue("endDate", endDateStr);
					form.setFieldValue("endTime", endTimeStr);

					// Re-trigger validation to clear any errors
					form.validateField("endDate", "change");
					form.validateField("endTime", "change");
				}
			} catch (error) {
				console.warn("Error adjusting end time:", error);
			}
		});
	}
};

const handleEndTimeChange = (field, value) => {
	// Round to nearest 15-minute interval
	const roundedValue = roundToNearestQuarter(value);
	
	field.handleChange(roundedValue);
	endTimeUserModified.value = true;

	// Recalculate duration after user changes end time
	nextTick(() => {
		try {
			const startDate = form.getFieldValue("startDate");
			const startTime = form.getFieldValue("startTime");
			const endDate = form.getFieldValue("endDate");
			const endTime = form.getFieldValue("endTime");

			if (startDate && startTime && endDate && endTime) {
				const startDateTime = new Date(`${startDate}T${startTime}`);
				const endDateTime = new Date(`${endDate}T${endTime}`);
				// Round duration to nearest 15-minute interval
				currentDuration.value = Math.round(
					(endDateTime - startDateTime) / (1000 * 60 * 15)
				) * 15;
			}
		} catch (error) {
			console.warn("Error calculating new duration:", error);
		}
	});
};

const handleEndDateChange = (field, value) => {
	field.handleChange(value);
	endTimeUserModified.value = true;

	// Recalculate duration after user changes end date
	nextTick(() => {
		try {
			const startDate = form.getFieldValue("startDate");
			const startTime = form.getFieldValue("startTime");
			const endDate = form.getFieldValue("endDate");
			const endTime = form.getFieldValue("endTime");

			if (startDate && startTime && endDate && endTime) {
				const startDateTime = new Date(`${startDate}T${startTime}`);
				const endDateTime = new Date(`${endDate}T${endTime}`);
				// Round duration to nearest 15-minute interval
				currentDuration.value = Math.round(
					(endDateTime - startDateTime) / (1000 * 60 * 15)
				) * 15;
			}
		} catch (error) {
			console.warn("Error calculating new duration:", error);
		}
	});
};

// Initialize duration when form is populated
watch(
	() => {
		try {
			const startDate = form.getFieldValue("startDate");
			const startTime = form.getFieldValue("startTime");
			const endDate = form.getFieldValue("endDate");
			const endTime = form.getFieldValue("endTime");
			return { startDate, startTime, endDate, endTime };
		} catch {
			return {};
		}
	},
	(values) => {
		if (
			values.startDate &&
			values.startTime &&
			values.endDate &&
			values.endTime &&
			currentDuration.value === null
		) {
			try {
				const startDateTime = new Date(
					`${values.startDate}T${values.startTime}`
				);
				const endDateTime = new Date(`${values.endDate}T${values.endTime}`);
				// Round duration to nearest 15-minute interval
				const duration = Math.round(
					(endDateTime - startDateTime) / (1000 * 60 * 15)
				) * 15;
				currentDuration.value = duration;
				endTimeUserModified.value = false;
			} catch (error) {
				console.warn("Error initializing duration:", error);
			}
		}
	},
	{ immediate: true }
);

// Reset duration state when modal opens/closes
watch(
	() => props.show,
	(isShowing) => {
		if (isShowing) {
			// Modal is opening - reset the state for fresh duration tracking
			currentDuration.value = null;
			endTimeUserModified.value = false;
		}
	}
);

// Track validation state to prevent infinite loops
let validationInProgress = false;

// Helper function to trigger validation of other fields (not self)
const triggerOtherFieldsValidation = (fieldApi, currentFieldName) => {
	if (validationInProgress) return;
	
	validationInProgress = true;
	nextTick(() => {
		const fields = ["startDate", "startTime", "endDate", "endTime"];
		fields.forEach(fieldName => {
			if (fieldName !== currentFieldName) {
				fieldApi.form.validateField(fieldName, "change");
			}
		});
		validationInProgress = false;
	});
};

// Validation functions
const validateStartDate = ({ value, fieldApi }) => {
	if (!value) return "Startdatum ist erforderlich";

	// Trigger validation of other fields when start date changes
	triggerOtherFieldsValidation(fieldApi, "startDate");

	return undefined;
};

const validateStartTime = ({ value, fieldApi }) => {
	if (!value) return "Startzeit ist erforderlich";

	// Trigger validation of other fields when start time changes
	triggerOtherFieldsValidation(fieldApi, "startTime");

	return undefined;
};

const validateEndDate = ({ value, fieldApi }) => {
	if (!value) return "Enddatum ist erforderlich";

	// Validate that end date/time is after start date/time
	const startDate = fieldApi.form.getFieldValue("startDate");
	const startTime = fieldApi.form.getFieldValue("startTime");
	const endTime = fieldApi.form.getFieldValue("endTime");

	if (startDate && startTime && endTime) {
		const startDateTime = new Date(`${startDate}T${startTime}`);
		const endDateTime = new Date(`${value}T${endTime}`);

		if (endDateTime <= startDateTime) {
			return "Ende kann nicht vor dem Start liegen";
		}

		// Check minimum duration (30 minutes)
		const duration = endDateTime - startDateTime;
		const minDuration = 30 * 60 * 1000; // 30 minutes

		if (duration < minDuration) {
			return "Buchung muss mindestens 30 Minuten lang sein";
		}
	}

	// Trigger validation of other fields when end date changes
	triggerOtherFieldsValidation(fieldApi, "endDate");

	return undefined;
};

const validateEndTime = ({ value, fieldApi }) => {
	if (!value) return "Endzeit ist erforderlich";

	// Validate that end date/time is after start date/time
	const startDate = fieldApi.form.getFieldValue("startDate");
	const startTime = fieldApi.form.getFieldValue("startTime");
	const endDate = fieldApi.form.getFieldValue("endDate");

	if (startDate && startTime && endDate) {
		const startDateTime = new Date(`${startDate}T${startTime}`);
		const endDateTime = new Date(`${endDate}T${value}`);

		if (endDateTime <= startDateTime) {
			return "Ende kann nicht vor dem Start liegen";
		}

		// Check minimum duration (30 minutes)
		const duration = endDateTime - startDateTime;
		const minDuration = 30 * 60 * 1000; // 30 minutes

		if (duration < minDuration) {
			return "Buchung muss mindestens 30 Minuten lang sein";
		}
	}

	// Trigger validation of other fields when end time changes
	triggerOtherFieldsValidation(fieldApi, "endTime");

	return undefined;
};

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createCommentVNode(" Start Date "),
    _createVNode(_unref(form).Field, {
      name: "startDate",
      validators: {
        onChange: validateStartDate,
      }
    }, {
      default: _withCtx(({ field }) => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[0] || (_cache[0] = _createElementVNode("label", {
              for: "startDate",
              class: "form-label"
            }, [
              _createTextVNode(" Startdatum "),
              _createElementVNode("span", { class: "text-danger" }, "*")
            ], -1 /* CACHED */)),
            _createElementVNode("input", {
              id: "startDate",
              name: field.name,
              value: field.state.value,
              type: "date",
              class: _normalizeClass(["form-control", { 'is-invalid': field.state.meta.errors.length > 0 }]),
              disabled: __props.disabled,
              onInput: (e) => handleStartDateChange(field, e.target.value),
              onBlur: field.handleBlur
            }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_4),
            (field.state.meta.errors.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(field.state.meta.errors[0]), 1 /* TEXT */))
              : _createCommentVNode("v-if", true)
          ]),
          _createCommentVNode(" Start Time "),
          _createVNode(_unref(form).Field, {
            name: "startTime",
            validators: {
              onChange: validateStartTime,
            }
          }, {
            default: _withCtx(({ field: timeField }) => [
              _createElementVNode("div", _hoisted_6, [
                _cache[1] || (_cache[1] = _createElementVNode("label", {
                  for: "startTime",
                  class: "form-label"
                }, [
                  _createTextVNode(" Startzeit "),
                  _createElementVNode("span", { class: "text-danger" }, "*")
                ], -1 /* CACHED */)),
                _createVNode(TimePickerPopover, {
                  "model-value": timeField.state.value,
                  name: timeField.name,
                  "input-id": "startTime",
                  "input-class": {
                    'is-invalid': timeField.state.meta.errors.length > 0,
                  },
                  disabled: __props.disabled,
                  placeholder: "Startzeit wählen",
                  "onUpdate:modelValue": (value) => handleStartTimeChange(timeField, value),
                  onBlur: timeField.handleBlur
                }, null, 8 /* PROPS */, ["model-value", "name", "input-class", "disabled", "onUpdate:modelValue", "onBlur"]),
                (timeField.state.meta.errors.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(timeField.state.meta.errors[0]), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true)
              ])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["validators"])
        ])
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["validators"]),
    _createCommentVNode(" End Date "),
    _createVNode(_unref(form).Field, {
      name: "endDate",
      validators: {
        onChange: validateEndDate,
      }
    }, {
      default: _withCtx(({ field }) => [
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            _cache[2] || (_cache[2] = _createElementVNode("label", {
              for: "endDate",
              class: "form-label"
            }, [
              _createTextVNode(" Enddatum "),
              _createElementVNode("span", { class: "text-danger" }, "*")
            ], -1 /* CACHED */)),
            _createElementVNode("input", {
              id: "endDate",
              name: field.name,
              value: field.state.value,
              type: "date",
              class: _normalizeClass(["form-control", { 'is-invalid': field.state.meta.errors.length > 0 }]),
              disabled: __props.disabled,
              onInput: (e) => handleEndDateChange(field, e.target.value),
              onBlur: field.handleBlur
            }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_10),
            (field.state.meta.errors.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(field.state.meta.errors[0]), 1 /* TEXT */))
              : _createCommentVNode("v-if", true)
          ]),
          _createCommentVNode(" End Time "),
          _createVNode(_unref(form).Field, {
            name: "endTime",
            validators: {
              onChange: validateEndTime,
            }
          }, {
            default: _withCtx(({ field: timeField }) => [
              _createElementVNode("div", _hoisted_12, [
                _cache[3] || (_cache[3] = _createElementVNode("label", {
                  for: "endTime",
                  class: "form-label"
                }, [
                  _createTextVNode(" Endzeit "),
                  _createElementVNode("span", { class: "text-danger" }, "*")
                ], -1 /* CACHED */)),
                _createVNode(TimePickerPopover, {
                  "model-value": timeField.state.value,
                  name: timeField.name,
                  "input-id": "endTime",
                  "input-class": {
                    'is-invalid': timeField.state.meta.errors.length > 0,
                  },
                  disabled: __props.disabled,
                  placeholder: "Endzeit wählen",
                  "onUpdate:modelValue": (value) => handleEndTimeChange(timeField, value),
                  onBlur: timeField.handleBlur
                }, null, 8 /* PROPS */, ["model-value", "name", "input-class", "disabled", "onUpdate:modelValue", "onBlur"]),
                (timeField.state.meta.errors.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(timeField.state.meta.errors[0]), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true)
              ])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["validators"])
        ])
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["validators"]),
    _createCommentVNode(" Ignore Opening Hours "),
    _createVNode(_unref(form).Field, { name: "ignoreOpeningHours" }, {
      default: _withCtx(({ field }) => [
        (__props.canIgnoreOpeningHours)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("input", {
                  id: "ignoreOpeningHours",
                  name: field.name,
                  checked: field.state.value,
                  type: "checkbox",
                  class: "form-check-input",
                  disabled: __props.disabled,
                  onChange: (e) => field.handleChange(e.target.checked),
                  onBlur: field.handleBlur
                }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_16),
                _cache[4] || (_cache[4] = _createElementVNode("label", {
                  for: "ignoreOpeningHours",
                  class: "form-check-label"
                }, " Öffnungszeiten ignorieren ", -1 /* CACHED */))
              ])
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }),
    _createCommentVNode(" Points preview for students - helps distinguish this from the walk-in form,\n         which explicitly states it consumes no points instead "),
    _createVNode(_unref(form).Subscribe, null, {
      default: _withCtx(({ values }) => [
        (_unref(masterData).userRole.value === 'student' && values.roomId)
          ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
              (canCreateWithoutTransactionInRoom(values.roomId))
                ? (_openBlock(), _createElementBlock("span", _hoisted_18, [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("strong", null, "Ohne Punktebelastung", -1 /* CACHED */),
                    _createTextVNode(" für diesen Raum ", -1 /* CACHED */)
                  ]))]))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createElementVNode("div", _hoisted_19, [
                      _cache[6] || (_cache[6] = _createTextVNode(" Benötigte Punkte: ", -1 /* CACHED */)),
                      _createElementVNode("strong", null, _toDisplayString(estimatedPoints(values)), 1 /* TEXT */)
                    ]),
                    (availablePoints.value !== null)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                          _cache[7] || (_cache[7] = _createTextVNode(" Verfügbare Punkte: ", -1 /* CACHED */)),
                          _createElementVNode("strong", null, _toDisplayString(availablePoints.value), 1 /* TEXT */)
                        ]))
                      : _createCommentVNode("v-if", true)
                  ], 64 /* STABLE_FRAGMENT */))
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    })
  ]))
}
}

}